// Join conditional class names — the pattern used across every component here.
export const cx = (...parts) => parts.flat(Infinity).filter(Boolean).join(" ");

// "Zapier Sync" -> "ZS". Fallback avatar when an app has no appImage.
export const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

// Deterministic accent per app so a card keeps the same colour across reloads.
const GRADIENTS = [
  "from-brand-500 to-violet-500",
  "from-sky-500 to-cyan-500",
  "from-emerald-500 to-teal-500",
  "from-amber-500 to-orange-500",
  "from-rose-500 to-pink-500",
  "from-fuchsia-500 to-purple-500",
];

export const gradientFor = (seed = "") => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return GRADIENTS[Math.abs(hash) % GRADIENTS.length];
};

export const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

// Date and time to the second, for log rows where the order of events matters.
export const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

// Show the first/last few characters of a secret so it can be identified
// without being readable over someone's shoulder.
export const maskSecret = (value = "") => {
  if (!value) return "";
  if (value.length <= 8) return "•".repeat(value.length);
  return `${value.slice(0, 4)}${"•".repeat(Math.min(value.length - 8, 24))}${value.slice(-4)}`;
};

// Turn a YouTube / Vimeo / Loom share link into something an <iframe> accepts.
// Anything unrecognised is returned untouched.
export const toEmbedUrl = (url = "") => {
  if (!url) return "";
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");

    if (host === "youtu.be") return `https://www.youtube.com/embed${u.pathname}`;
    if (host.endsWith("youtube.com")) {
      if (u.pathname.startsWith("/embed/")) return url;
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
    }
    if (host.endsWith("vimeo.com") && !host.startsWith("player.")) {
      const id = u.pathname.split("/").filter(Boolean)[0];
      return id ? `https://player.vimeo.com/video/${id}` : url;
    }
    if (host.endsWith("loom.com")) return url.replace("/share/", "/embed/");
    return url;
  } catch {
    return url;
  }
};

// The marketplace install screen for an app.
export const buildInstallUrl = (marketplaceUrl, appId) =>
  appId ? `${marketplaceUrl}/apps/${appId}` : marketplaceUrl;

// navigator.clipboard needs a secure context, which the GHL iframe does not
// always provide — fall back to the legacy execCommand path.
export const copyText = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  } catch {
    return false;
  }
};
